import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Campo } from '@/shared/ui/Formulario/Campo'
import { Modal } from '@/shared/ui/Modal/Modal'

import estilos from './NuevoServicio.module.css'

type Props = {
  abierto: boolean
  onCerrar: () => void
  /** El id del servicio recién creado, para dejarlo escogido. */
  onCreado: (id: string) => void
}

/**
 * Un servicio nuevo, sin salir de la orden.
 *
 * MEGA lo pidió así y tiene razón: el catálogo de servicios no merecía una
 * pantalla propia en el menú. Son once filas que se tocan dos veces al año, y
 * SIEMPRE en el mismo momento — cuando alguien va a crear una orden y el
 * servicio que necesita no está en la lista. Esta semana pasó: faltaban
 * cuatro (Provisional de obra, Mantenimiento, Redes, Grúa) y tocó agregarlos.
 *
 * Así que vive donde nace la necesidad. Se crea, queda escogido en la orden
 * que se estaba armando, y se sigue.
 *
 * LO MÍNIMO PARA QUE SIRVA, no todo lo que la tabla admite. El porcentaje de
 * mano de obra y las horas de referencia se afinan después con el histórico;
 * pedirlos aquí frenaría a alguien que solo quiere registrar un trabajo.
 */
export function NuevoServicio({ abierto, onCerrar, onCreado }: Props) {
  const clienteConsultas = useQueryClient()

  const [codigo, setCodigo] = useState('')
  const [nombre, setNombre] = useState('')
  const [error, setError] = useState<string | null>(null)

  const crear = useMutation({
    mutationFn: async (): Promise<string> => {
      const { data, error: fallo } = await supabase
        .from('servicios')
        .insert({
          // El código va en mayúsculas siempre: es como se nombran entre
          // ellos y como salen en los informes.
          codigo: codigo.trim().toUpperCase(),
          nombre: nombre.trim(),
        } as never)
        .select('id')
        .single()

      if (fallo) throw new Error(mensajeDeError(fallo))

      return (data as { id: string }).id
    },
    onSuccess: async (id) => {
      await clienteConsultas.invalidateQueries({ queryKey: ['opciones', 'servicios'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['maestro', 'servicios'] })

      onCreado(id)
      setCodigo('')
      setNombre('')
      onCerrar()
    },
  })

  async function guardar() {
    setError(null)

    if (codigo.trim().length < 2 || nombre.trim().length < 3) {
      setError('El código y el nombre son obligatorios.')
      return
    }

    try {
      await crear.mutateAsync()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo crear el servicio.')
    }
  }

  return (
    <Modal
      abierto={abierto}
      titulo="Nuevo servicio"
      subtitulo="Queda escogido en la orden que está armando"
      onCerrar={onCerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton onClick={() => void guardar()} trabajando={crear.isPending}>
            Crear
          </Boton>
        </>
      }
    >
      {error && <Aviso tipo="error">{error}</Aviso>}

      <div className={estilos.campos}>
        <Campo
          etiqueta="Código"
          obligatorio
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.toUpperCase())}
          placeholder="INT"
          ayuda="Tres letras. Es como sale en los informes y en las tarjetas."
        />

        <Campo
          etiqueta="Nombre"
          obligatorio
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Instalaciones internas"
        />
      </div>

      <p className={estilos.nota}>
        Las horas de referencia y el porcentaje de mano de obra se ajustan después, cuando el
        servicio tenga histórico con qué compararse.
      </p>
    </Modal>
  )
}
